import { useEffect, useState } from 'react';
import { X, Settings } from 'lucide-react';
import { useCookieConsent, defaultPreferences, type CookiePreferences } from './CookieConsent';

export default function CookieBanner() {
  const { consent, bannerOpen, modalOpen, acceptAll, rejectAll, savePreferences, openModal, closeModal } =
    useCookieConsent();

  const [draft, setDraft] = useState<CookiePreferences>(defaultPreferences);

  useEffect(() => {
    if (modalOpen) setDraft(consent?.preferences ?? defaultPreferences);
  }, [modalOpen, consent]);

  if (!bannerOpen && !modalOpen) return null;

  return (
    <>
      {bannerOpen && (
        <div
          role="dialog"
          aria-live="polite"
          className="fixed bottom-4 left-1/2 z-50 w-[calc(100%-2rem)] max-w-xl -translate-x-1/2 rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl sm:bottom-6 sm:max-w-2xl"
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-slate-700">
              <p className="font-semibold text-slate-900">We value your privacy</p>
              <p className="mt-1">
                We use cookies to improve your experience and understand how our site is used. You can
                choose which cookies you are comfortable with.
              </p>
            </div>
            <div className="grid w-full gap-2 sm:grid-cols-3">
              <button
                onClick={openModal}
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                <Settings className="h-4 w-4" /> Customize
              </button>
              <button
                onClick={acceptAll}
                className="w-full rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-700"
              >
                Accept all
              </button>
              <button
                onClick={rejectAll}
                className="w-full rounded-lg bg-slate-100 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-200"
              >
                Reject all
              </button>
            </div>
          </div>
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur">
          <div className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl sm:p-8" role="dialog" aria-modal="true" aria-labelledby="cookie-title">
            <div className="flex items-center justify-between">
              <h2 id="cookie-title" className="text-2xl font-bold text-slate-900">Cookie preferences</h2>
              <button onClick={closeModal} className="rounded-full p-2 text-slate-500 hover:bg-slate-100" aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="mt-2 text-sm text-slate-600">
              Manage how we use cookies. Necessary cookies are always on because they keep the site working.
            </p>
            <div className="mt-6 space-y-4">
              {[
                { key: 'necessary' as const, label: 'Necessary', desc: 'Essential for the site to work. Cannot be turned off.' },
                { key: 'functional' as const, label: 'Functional', desc: 'Remember preferences and make the experience better.' },
                { key: 'analytics' as const, label: 'Analytics', desc: 'Help us understand how visitors use the site.' },
                { key: 'marketing' as const, label: 'Marketing', desc: 'Used to deliver relevant ads and measure campaigns.' },
              ].map((c) => (
                <label
                  key={c.key}
                  className="flex cursor-pointer items-start justify-between rounded-2xl border border-slate-100 bg-slate-50 p-4"
                >
                  <div>
                    <span className="font-bold text-slate-900">{c.label}</span>
                    <p className="mt-1 text-xs text-slate-600">{c.desc}</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={draft[c.key]}
                    disabled={c.key === 'necessary'}
                    onChange={(e) => setDraft({ ...draft, [c.key]: e.target.checked })}
                    className="mt-1 h-5 w-5 accent-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                </label>
              ))}
            </div>
            <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                onClick={rejectAll}
                className="rounded-full bg-slate-100 px-6 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-200"
              >
                Reject all
              </button>
              <button
                onClick={() => savePreferences(draft)}
                className="rounded-full bg-brand-600 px-6 py-2.5 text-sm font-bold text-white transition hover:bg-brand-700"
              >
                Save preferences
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
